/**
 * PublicKeysProvider - Fetches and provides public API keys from Supabase edge function.
 * Matches mobile's pattern of loading keys on app startup.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export interface PublicKeys {
  mapbox_public_key: string;
  turnstile_site_key: string;
  supabase_url: string;
  supabase_anon_key: string;
}

interface PublicKeysContextType {
  keys: PublicKeys | null;
  isLoading: boolean;
  error: Error | null;
}

const PublicKeysContext = createContext<PublicKeysContextType>({
  keys: null,
  isLoading: true,
  error: null,
});

export const usePublicKeys = () => useContext(PublicKeysContext);

interface PublicKeysProviderProps {
  children: ReactNode;
}

export function PublicKeysProvider({ children }: PublicKeysProviderProps) {
  const [keys, setKeys] = useState<PublicKeys | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchPublicKeys() {
      try {
        // Get the Supabase URL from the client
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        if (!supabaseUrl || !supabaseAnonKey) {
          throw new Error("Supabase URL or Anon Key not configured");
        }

        // Fetch public keys from edge function (same pattern as mobile)
        const response = await fetch(
          `${supabaseUrl}/functions/v1/get-public-keys?platform=web`,
          {
            headers: {
              Authorization: `Bearer ${supabaseAnonKey}`,
            },
          }
        );

        if (!response.ok) {
          // For local development, fall back to env variables if edge function fails (CORS)
          if (import.meta.env.DEV) {
            console.warn(
              "[PublicKeysProvider] Edge function failed, using env variable fallback for dev"
            );
            const fallbackKeys: PublicKeys = {
              mapbox_public_key: import.meta.env.VITE_MAPBOX_PUBLIC_KEY || "",
              turnstile_site_key: import.meta.env.VITE_TURNSTILE_SITE_KEY || "",

              supabase_url: supabaseUrl,
              supabase_anon_key: supabaseAnonKey,
            };
            setKeys(fallbackKeys);
            return;
          }
          throw new Error(`Failed to fetch public keys: ${response.statusText}`);
        }

        const data = await response.json();
        setKeys(data);
      } catch (err) {
        console.error("[PublicKeysProvider] Error fetching public keys:", err);

        // For local development, fall back to env variables
        if (import.meta.env.DEV) {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

          console.warn(
            "[PublicKeysProvider] Using env variable fallback for dev"
          );
          const fallbackKeys: PublicKeys = {
            mapbox_public_key: import.meta.env.VITE_MAPBOX_PUBLIC_KEY || "",
            turnstile_site_key: import.meta.env.VITE_TURNSTILE_SITE_KEY || "",
            supabase_url: supabaseUrl || "",
            supabase_anon_key: supabaseAnonKey || "",
          };
          setKeys(fallbackKeys);
          return;
        }

        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    }

    fetchPublicKeys();
  }, []);

  return (
    <PublicKeysContext.Provider value={{ keys, isLoading, error }}>
      {children}
    </PublicKeysContext.Provider>
  );
}
